import {Injectable}               from '@angular/core';
import {OperationModesEnum}       from '../../stores/common/operation-modes.store';
import {ApplicationSettingsStore} from '../../stores/agronomic/application-settings-store.service';
import IApplicationSettings = Data.Agronomic.IApplicationSettings;

@Injectable({providedIn: 'root'})
export class ApplicationSettingsService {
  constructor(private applicationSettingsStore: ApplicationSettingsStore) {
  }

  /**
   * Checks if an EC value falls within the valid range of application settings
   * @param ecValue The EC (growth stage) value to validate
   * @param applicationSettings The application settings containing EC range
   * @returns Boolean indicating whether the EC value is valid for the given application settings
   * @public
   */
  public isApplicationSettingValidForGrowthStage(ecValue: number | undefined,
                                                 applicationSettings: IApplicationSettings | undefined): boolean {
    if (!applicationSettings || !Number.isFinite(ecValue)) {
      return false;
    }
    return applicationSettings.EcMin <= ecValue && ecValue <= applicationSettings.EcMax;
  }

  /**
   * Retrieves application settings for a specific operation mode and crop (EPPO code)
   * @param operationModeKey The operation mode to filter application settings
   * @param eppoCode The crop's EPPO code to match against application settings
   * @returns Matching application settings or undefined if no match is found
   * @public
   */
  public getApplicationSettings(operationModeKey: OperationModesEnum, eppoCode: string): IApplicationSettings | undefined {
    if (!this.isValidOperationMode(operationModeKey) || !eppoCode) {
      return undefined;
    }
    const applicationSettingsByOperationMode = this.applicationSettingsStore.Applications.filter(x => x.OperationMode.Key === operationModeKey);
    if (applicationSettingsByOperationMode.length <= 0) {
      return undefined;
    }
    return applicationSettingsByOperationMode.find(x => {
      return x.EppoGroup.EppoCodes.some(y => y === eppoCode);
    });
  }

  /**
   * Finds all agronomic.applicationSettings which apply for the given combination of eppoCode and ec-values (of selected fields/sensor data)
   * @param operationModeKey operation mode for the application settings
   * @param eppoCode the field's eppoCode
   * @param ecValues ec values of all selected fields
   * @public
   */
  public getApplicationSettingsForEppoAndEcRange(operationModeKey: OperationModesEnum, eppoCode: string, ecValues: number[]): IApplicationSettings[] {
    if (!this.isValidOperationMode(operationModeKey) || !eppoCode) {
      return [];
    }
    const applicationSettingsByOperationMode = this.applicationSettingsStore.Applications.filter(x => x.OperationMode.Key === operationModeKey);
    if (applicationSettingsByOperationMode.length <= 0) {
      return [];
    }
    return applicationSettingsByOperationMode.FindAll(x => {
      return x.EppoGroup.EppoCodes.some(y => y === eppoCode) && ecValues.some(y => y >= x.EcMin && y <= x.EcMax);
    });
  }

  /**
   * Validates if the given operation mode is supported for application settings
   * Checks against a predefined list of valid operation modes for specific agronomic applications
   * @param operationModeKey The operation mode to validate
   * @returns Boolean indicating whether the operation mode is valid for application settings
   * @private
   */
  private isValidOperationMode(operationModeKey: OperationModesEnum): boolean {
    return operationModeKey === OperationModesEnum.PpTarget
      || operationModeKey === OperationModesEnum.ApplicationMap
      || operationModeKey === OperationModesEnum.ApplicationMapAbsolute
      || operationModeKey === OperationModesEnum.NfTargetMapPerPlan
      || operationModeKey === OperationModesEnum.PpTargetMapPerField
      || operationModeKey === OperationModesEnum.PPTargetMapPerPlan;
  }
}
